import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { parkingZones } from '../services/api';
import concessionsApi from '../services/concessions';
import SearchableSelect from './SearchableSelect';

// Compresse une image en base64 (max 1024px, JPEG 0.85)
async function fileToCompressedBase64(file) {
  if (!file || !file.type.startsWith('image/')) return null;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 1024;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image invalide')); };
    img.src = url;
  });
}

function ZonesParking({ user, editMode, setHeaderAction }) {
  const [concessions, setConcessions] = useState([]);
  const [concessionId, setConcessionId] = useState(null);
  const [zones, setZones] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', zone? }
  const [form, setForm] = useState({ nom: '', ordre: 0, image_data: null, imageDirty: false });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.role === 'super-admin';
  const canEdit = editMode === true && isSuperAdmin;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await concessionsApi.getAll();
        if (cancelled) return;
        const list = res.data.concessions || res.data || [];
        setConcessions(list);
        if (list.length > 0 && !concessionId) {
          setConcessionId(list[0].id);
        }
      } catch (e) {
        console.error(e);
        setError('Impossible de charger les concessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadZones = useCallback(async () => {
    if (!concessionId) { setZones([]); return; }
    try {
      const res = await parkingZones.list(concessionId);
      setZones(res.data.zones || []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les zones');
    }
  }, [concessionId]);

  useEffect(() => { loadZones(); }, [loadZones]);

  const openCreate = useCallback(() => {
    if (!isSuperAdmin) return;
    setForm({ nom: '', ordre: zones.length + 1, image_data: null, imageDirty: false });
    setModal({ mode: 'create' });
  }, [isSuperAdmin, zones.length]);

  useEffect(() => {
    if (!setHeaderAction) return;
    if (isSuperAdmin && concessionId) {
      setHeaderAction({
        primary: { label: '+ Ajouter une zone', onClick: openCreate },
      });
    } else {
      setHeaderAction(null);
    }
    return () => setHeaderAction && setHeaderAction(null);
  }, [setHeaderAction, isSuperAdmin, concessionId, openCreate]);

  const openEdit = (zone) => {
    if (!isSuperAdmin) return;
    setForm({ nom: zone.nom, ordre: zone.ordre, image_data: zone.image_data || null, imageDirty: false });
    setModal({ mode: 'edit', zone });
  };

  const closeModal = () => {
    setModal(null);
    setForm({ nom: '', ordre: 0, image_data: null, imageDirty: false });
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const b64 = await fileToCompressedBase64(file);
      setForm(prev => ({ ...prev, image_data: b64, imageDirty: true }));
    } catch (err) {
      alert('Image invalide ou trop volumineuse');
    }
  };

  const removeImage = () => {
    setForm(prev => ({ ...prev, image_data: null, imageDirty: true }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return;
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const payload = {
          concession_id: concessionId,
          nom: form.nom.trim(),
          ordre: parseInt(form.ordre, 10) || 0,
        };
        if (form.image_data) payload.image_data = form.image_data;
        await parkingZones.create(payload);
      } else {
        const payload = {
          nom: form.nom.trim(),
          ordre: parseInt(form.ordre, 10) || 0,
        };
        if (form.imageDirty) payload.image_data = form.image_data;
        await parkingZones.update(modal.zone.id, payload);
      }
      await loadZones();
      closeModal();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || 'Erreur lors de l\'enregistrement';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (zone) => {
    if (!canEdit) return;
    if (!window.confirm(`Supprimer la zone « ${zone.nom} » ?\nLes véhicules déjà associés à cette zone resteront visibles avec leur libellé actuel, mais elle ne sera plus proposée dans la webapp.`)) return;
    try {
      await parkingZones.remove(zone.id);
      await loadZones();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const filteredZones = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return zones;
    return zones.filter(z => z.nom.toLowerCase().includes(q));
  }, [zones, search]);

  const selectedConcession = concessions.find(c => c.id === concessionId);
  const concessionOptions = concessions.map(c => ({ value: c.id, label: c.nom }));

  if (loading) return <p>Chargement…</p>;
  if (error) return <p style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>{error}</p>;

  return (
    <>
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher une zone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <SearchableSelect
          value={concessionId}
          options={concessionOptions}
          onChange={setConcessionId}
          placeholder="Choisir une concession"
        />
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {filteredZones.length} zone(s){selectedConcession ? ` • ${selectedConcession.nom}` : ''}
        </span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '80px' }}>Ordre</th>
              <th style={{ width: '90px' }}>Image</th>
              <th>Nom</th>
              <th style={{ width: '120px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredZones.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                  {concessionId ? 'Aucune zone configurée pour cette concession.' : 'Sélectionnez une concession.'}
                </td>
              </tr>
            ) : filteredZones.map(z => (
              <tr key={z.id}>
                <td>{z.ordre}</td>
                <td>
                  {z.image_data ? (
                    <img src={z.image_data} alt={z.nom} style={{ width: '64px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-default)' }} />
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '20px' }}>📍</span>
                  )}
                </td>
                <td>{z.nom}</td>
                <td>
                  <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                    {isSuperAdmin && (
                      <button className="btn-icon" title="Modifier" onClick={() => openEdit(z)}>✏️</button>
                    )}
                    {canEdit && (
                      <button className="btn-icon delete" title="Supprimer" onClick={() => remove(z)}>🗑️</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal.mode === 'create' ? 'Nouvelle zone' : 'Modifier la zone'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Concession
                  </label>
                  <input
                    type="text"
                    value={selectedConcession?.nom || ''}
                    disabled
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: '6px', background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Nom de la zone *
                  </label>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={e => setForm({ ...form, nom: e.target.value })}
                    autoFocus
                    required
                    maxLength={100}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Ordre d'affichage
                  </label>
                  <input
                    type="number"
                    value={form.ordre}
                    onChange={e => setForm({ ...form, ordre: e.target.value })}
                    style={{ width: '120px', padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Image de la zone (optionnelle)
                  </label>
                  {form.image_data ? (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <img
                        src={form.image_data}
                        alt="Aperçu"
                        style={{ width: '180px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-default)' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label className="btn-secondary" style={{ cursor: 'pointer' }}>
                          📷 Remplacer
                          <input type="file" accept="image/*" onChange={onPickImage} style={{ display: 'none' }} />
                        </label>
                        <button type="button" className="btn-secondary" onClick={removeImage}>
                          🗑️ Retirer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                      📷 Ajouter une image
                      <input type="file" accept="image/*" onChange={onPickImage} style={{ display: 'none' }} />
                    </label>
                  )}
                  <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Compressée automatiquement (max 1024px, JPEG).
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>Annuler</button>
                <button type="submit" className="btn-primary" disabled={saving || !form.nom.trim()}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default ZonesParking;
